// DOM wiring for the automatic post table of contents. Pure outline/markup
// logic lives in toc-lib.js — this file only touches the DOM: scanning
// headings, injecting the desktop nav + mobile bar, and click-to-scroll.
// See docs/superpowers/specs/2026-07-24-post-table-of-contents-design.md.
import { MIN_H2_COUNT, buildOutline, renderTocListHtml } from './toc-lib';

function extractHeadings(source) {
    return Array.from(source.querySelectorAll('h2[id], h3[id]')).map((el) => ({
        level: el.tagName === 'H2' ? 2 : 3,
        id: el.id,
        text: el.textContent.trim(),
    }));
}

function buildDesktopNav(listHtml) {
    const nav = document.createElement('nav');
    nav.className = 'gh-toc';
    nav.setAttribute('aria-label', 'Table of contents');
    nav.innerHTML = listHtml;
    return nav;
}

function buildMobileBar(listHtml) {
    const wrap = document.createElement('div');
    wrap.className = 'gh-toc-mobile';
    wrap.innerHTML =
        '<button class="gh-toc-mobile__toggle" type="button" aria-expanded="false">' +
        'Table of contents' +
        '<svg class="gh-toc-mobile__chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">' +
        '<path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" /></svg>' +
        '</button>' +
        `<div class="gh-toc-mobile__panel">${listHtml}</div>`;
    return wrap;
}

function wireToggle(mobile) {
    const toggle = mobile.querySelector('.gh-toc-mobile__toggle');
    toggle.addEventListener('click', () => {
        const isOpen = mobile.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
    });
}

function closeMobile(mobile) {
    mobile.classList.remove('is-open');
    mobile.querySelector('.gh-toc-mobile__toggle').setAttribute('aria-expanded', 'false');
}

function wireJumpLinks(wrapper, mobile) {
    wrapper.querySelectorAll('[data-toc-target]').forEach((link) => {
        link.addEventListener('click', (event) => {
            event.preventDefault();
            const target = document.getElementById(link.dataset.tocTarget);
            if (target) target.scrollIntoView({ behavior: 'smooth' });
            closeMobile(mobile);
        });
    });
}

function centerInContainer(container, activeLink) {
    // Manual scrollTop math rather than activeLink.scrollIntoView(): with
    // .gh-toc capped at max-height + overflow-y (see toc.css) there are
    // two nested scroll boxes — the page and the TOC's own internal
    // scroll — and scrollIntoView's ancestor-walking behavior isn't
    // guaranteed to touch only the inner one. Computing the delta
    // directly and calling scrollTo only on `container` can't leak into
    // the outer page scroll.
    if (!container || !activeLink) return;
    const containerRect = container.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    const offset = linkRect.top - containerRect.top - containerRect.height / 2 + linkRect.height / 2;
    container.scrollTo({ top: container.scrollTop + offset, behavior: 'smooth' });
}

function setActiveTarget(article, id) {
    article.querySelectorAll('[data-toc-target]').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.tocTarget === id);
    });

    const nav = article.querySelector('.gh-toc');
    centerInContainer(nav, nav && nav.querySelector('.is-active'));

    const mobilePanel = article.querySelector('.gh-toc-mobile__panel');
    centerInContainer(mobilePanel, mobilePanel && mobilePanel.querySelector('.is-active'));
}

function watchHeadings(article, headingEls) {
    const observer = new IntersectionObserver(
        (entries) => {
            const visible = entries.filter((entry) => entry.isIntersecting);
            if (visible.length === 0) return;
            visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            setActiveTarget(article, visible[0].target.id);
        },
        // Shrinks the observed viewport so a heading only counts as
        // "active" once it has scrolled below the fixed TOC's top offset,
        // and stops counting once it's past the upper 30% of the viewport
        // — picks the section actually being read, not just barely
        // visible at the very bottom of the screen.
        { rootMargin: '-140px 0px -70% 0px' }
    );
    headingEls.forEach((el) => observer.observe(el));
}

function watchRelease(article, nav) {
    // .gh-toc is `position: fixed` so it's visible immediately on page
    // load, regardless of what precedes it (page-header, feature image).
    // Once continuing to stay fixed at top: 140px would carry the TOC's
    // own bottom edge past the article's bottom edge, switch it to
    // `position: absolute; bottom: 0` (anchored to .gh-article) instead,
    // so it stops exactly at the article's end rather than overlapping
    // whatever follows (comments, related-post cards, footer).
    //
    // This has to be scroll-driven rather than an IntersectionObserver:
    // the release threshold depends on the TOC's own measured height
    // (nav.offsetHeight), a second, independently-sized element — a
    // rootMargin can express a fixed line, but not one relative to
    // another element's height, and a fixed-line version released too
    // early, then un-released again as soon as the sentinel scrolled
    // above it, re-overlapping the footer it was meant to avoid.
    let ticking = false;

    function update() {
        ticking = false;
        const articleBottom = article.getBoundingClientRect().bottom;
        nav.classList.toggle('is-released', articleBottom <= 140 + nav.offsetHeight);
    }

    function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
}

export default function buildToc() {
    const source = document.querySelector('[data-toc-source]');
    if (!source) return;

    const article = source.closest('.gh-article');
    if (!article) return;

    const headings = extractHeadings(source);
    const outline = buildOutline(headings);
    if (outline.length < MIN_H2_COUNT) return;

    const listHtml = renderTocListHtml(outline);
    const nav = buildDesktopNav(listHtml);
    const mobile = buildMobileBar(listHtml);

    article.appendChild(nav);
    article.appendChild(mobile);

    wireToggle(mobile);
    wireJumpLinks(article, mobile);

    const headingEls = headings.map((h) => document.getElementById(h.id)).filter(Boolean);
    watchHeadings(article, headingEls);
    watchRelease(article, nav);
}
